import React, { useState, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import {
  entity_id,
  ApplicationType,
  Platform,
  locationGeo,
} from "../Utils/Const";
import { baseapi } from "../Environmentalurl";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";
 
const GREEN = "#0f9d58";
const GREEN_DK = "#0b7c46";
const RED = "#ef4444";
const RED_DK = "#dc2626";
 
const REASONS = ["Blocked", "Hot Listed", "Card Lost", "Card Stolen"];
 
// ---------------------------------------------------
// Formats current date/time as "YYYY-MM-DD HH:mm:ss"
// ---------------------------------------------------
const getFormattedDate = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
 
// ---------------------------------------------------
// Robustly checks a Response_Code against an expected
// success code — handles number vs string vs stray
// whitespace from the backend ("1000", 1000, " 1000 ").
// ---------------------------------------------------
const isSuccessCode = (code, expected = "1000") =>
  String(code ?? "").trim() === String(expected);
 
// ---------------------------------------------------
// Same idea, but accepts a LIST of success codes.
// Some endpoints (e.g. UpdateChannelStatus) return
// "9000" on success instead of "1000".
// ---------------------------------------------------
const isAnySuccessCode = (code, expected = ["1000"]) =>
  expected.map(String).includes(String(code ?? "").trim());
 
export default function BlockCardDialog({ open = true, onClose }) {
  const ip = useIPAddress();
 
  const [view, setView] = useState("home"); // "home" | "permanent"
  const [cardEnabled, setCardEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
 
  // ---- confirmation state for the enable/disable switch ----
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState(null); // the state being confirmed
 
  const [pin, setPin] = useState(["", "", "", ""]);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
 
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
 
  const referenceNumber = sessionStorage.getItem("rupayRefNo") || "";
  const mobileNumber = sessionStorage.getItem("mobileNumber") || "";
  const sessionID = sessionStorage.getItem("SessionID") || "";
 
  const pinRefs = useRef([]);
  const pinStr = pin.join("");
 
  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
 
  /* ---------------- PIN handlers ---------------- */
  const handlePinChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...pin];
    next[idx] = val;
    setPin(next);
    if (val && idx < 3) pinRefs.current[idx + 1]?.focus();
  };
 
  const handlePinKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !pin[idx] && idx > 0) {
      pinRefs.current[idx - 1]?.focus();
    }
  };
 
  /* ---------------- RPIN encryption chain ---------------- */
  const getCardStatus = async () => {
    const res = await api.post(`${baseapi}/getcardstatus`, {
      ReqType: "Jan_Aadhaar_eWallet_GetCardStatus",
      Mobilenumber: mobileNumber,
      ReferenceNumber: referenceNumber,
      Platform,
      ApplicationType,
      EntityID: entity_id,
      GeoLocation: locationGeo,
      MachineID: ip,
      SessionID: sessionID,
    });
    return { maskedCard: res.data.MaskedCard, encKey: res.data.EncKey };
  };
 
  const getHexXor = async () => {
    const res = await api.post(`${baseapi}/hexXorgenerator`, {
      rpin: pinStr,
      mobilenumber: mobileNumber,
    });
    return res.data;
  };
 
  const encryptRpin = async (hexXor, encKey) => {
    const res = await api.post(`${baseapi}/ewalletRupayEncryption3DES`, {
      ReqType: "Jan_Aadhaar_eWallet_RupayEncryption3DES",
      Mobilenumber: mobileNumber,
      Enc_Key: encKey,
      Date: getFormattedDate(),
      Hex_XOR_Value: hexXor,
      Platform,
      SessionID: sessionID,
      ApplicationType,
      EntityID: entity_id,
      GeoLocation: locationGeo,
      MachineID: ip,
    });
    return res.data.RPIN;
  };
 
  /* ---------------- Switch click → open confirmation ---------------- */
  const handleSwitchChange = (e) => {
    if (toggling) return;
    setPendingEnabled(e.target.checked); // intended next state
    setConfirmOpen(true);
  };
 
  const cancelToggle = () => {
    setConfirmOpen(false);
    setPendingEnabled(null);
    // switch stays as-is since it's controlled by cardEnabled, untouched
  };
 
  /* ---------------- Enable / Disable toggle (called after confirm) ---------------- */
  const confirmToggleStatus = async () => {
    const nextEnabled = pendingEnabled;
    if (nextEnabled === null) return;
 
    setConfirmOpen(false);
    setToggling(true);
    try {
      const payload = {
        ReqType: "Jan_Aadhaar_eWallet_UpdateChannelStatus",
        Mobilenumber: mobileNumber,
        ReferenceNumber: referenceNumber,
        SessionID: sessionID,
        // Only the Online channel is controlled by this switch —
        // POS/NFC/ATM stay false per the card's usage pattern.
        POS: false,
        NFC: false,
        Online: nextEnabled,
        ATM: false,
        Date: getFormattedDate(),
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      };
 
      const res = await api.post(
        `${baseapi}/JanAadhaareWalletUpdateChannelStatus`,
        payload,
      );
 
      // Debug aid — check your browser console to see exactly what
      // the API returned if this ever misbehaves again.
      console.log("UpdateChannelStatus response:", res?.data);
 
      const { Response_Code, Message } = res?.data || {};
 
      // This endpoint returns "9000" on success (not "1000"),
      // so accept both codes here.
      if (isAnySuccessCode(Response_Code, ["1000", "9000"])) {
        setCardEnabled(nextEnabled);
        showSnackbar(
          Message || "Channel status updated successfully",
          "success",
        );
      } else {
        // Keep previous state, surface failure message
        showSnackbar(Message || "Failed to update channel status", "error");
      }
    } catch (err) {
      console.error("Toggle status error:", err);
      showSnackbar("System error. Please try again later.", "error");
    } finally {
      setToggling(false);
      setPendingEnabled(null);
    }
  };
 
  /* ---------------- Permanent block ---------------- */
  const handleBlockCard = async () => {
    if (pinStr.length !== 4 || !reason) return;
 
    setLoading(true);
    setResult(null);
    try {
      const { maskedCard, encKey } = await getCardStatus();
      const hexXor = await getHexXor();
      const encryptedPin = await encryptRpin(hexXor, encKey);
 
      const payload = {
        ReqType: "Jan_Aadhaar_eWallet_RuPay_Block_Card",
        Mobilenumber: mobileNumber,
        Virtual_Account: "999999901902778",
        ReasonToBlock: reason,
        BlockTypeID: 5,
        R_PIN: encryptedPin,
        ReferenceNumber: referenceNumber,
        CardNumber: maskedCard,
        SessionID: sessionID,
        Date: getFormattedDate(),
        Platform,
        ApplicationType,
        EntityID: entity_id,
        GeoLocation: locationGeo,
        MachineID: ip,
      };
 
      const res = await api.post(`${baseapi}/eWalletRuPayblockcard`, payload);
      const { Response_Code, Message } = res.data;
 
      if (isSuccessCode(Response_Code, "1000")) {
        setResult({
          success: true,
          message: Message || "Card blocked permanently.",
        });
      } else {
        setResult({
          success: false,
          message: Message || "Failed to block card.",
        });
      }
    } catch (err) {
      console.error("BLOCK CARD ERROR:", err);
      setResult({
        success: false,
        message: "System error. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };
 
  const resetAll = () => {
    setView("home");
    setPin(["", "", "", ""]);
    setReason("");
    setResult(null);
  };
 
  const handleClose = () => {
    resetAll();
    onClose?.();
  };
 
  const goBack = () => {
    if (result) {
      if (result.success) return handleClose();
      setResult(null);
      return;
    }
    if (view === "permanent") {
      setView("home");
      setPin(["", "", "", ""]);
      setReason("");
    } else {
      handleClose();
    }
  };
 
  const isPermanent = view === "permanent" && !result;
 
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        scroll="body"
        PaperProps={{
          sx: {
            width: 440,
            maxWidth: "94vw",
            borderRadius: "20px",
            overflow: "hidden",
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.4)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 2,
            color: "#fff",
            background: isPermanent
              ? "linear-gradient(135deg, #dc2626, #ef4444)"
              : "linear-gradient(135deg, #0b7c46, #0f9d58)",
          }}
        >
          <IconButton onClick={goBack} sx={{ color: "#fff" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            fontWeight={700}
            fontSize={17}
            flex={1}
            textAlign="center"
            sx={{ pr: 5 }}
          >
            {result
              ? result.success
                ? "Card Blocked"
                : "Action Failed"
              : isPermanent
                ? "Block Card Permanently"
                : "Manage RuPay Card"}
          </Typography>
        </Box>
 
        {/* Body */}
        <Box sx={{ p: 3 }}>
          {/* ---------- RESULT ---------- */}
          {result ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              {result.success ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 84, color: GREEN }} />
              ) : (
                <CancelRoundedIcon sx={{ fontSize: 84, color: RED }} />
              )}
              <Typography variant="h6" fontWeight={800} mt={1}>
                {result.success ? "Card Blocked" : "Action Failed"}
              </Typography>
              <Typography color="text.secondary" fontSize={14} mt={1} mb={3}>
                {result.message}
              </Typography>
              <Button
                fullWidth
                onClick={() =>
                  result.success ? handleClose() : setResult(null)
                }
                sx={btnSx(
                  result.success ? GREEN : RED,
                  result.success ? GREEN_DK : RED_DK,
                )}
              >
                {result.success ? "Done" : "Try Again"}
              </Button>
            </Box>
          ) : view === "home" ? (
            /* ---------- HOME ---------- */
            <>
              {/* Card visual */}
              <Box
                sx={{
                  borderRadius: 4,
                  p: 3,
                  mb: 3,
                  color: "#fff",
                  background: cardEnabled
                    ? "linear-gradient(135deg, #0b7c46 0%, #0f9d58 60%, #34d399 100%)"
                    : "linear-gradient(135deg, #475569 0%, #64748b 100%)",
                  boxShadow: "0 14px 30px -14px rgba(15,157,88,0.5)",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <CreditCardIcon />
                  <Typography fontSize={12} fontWeight={700}>
                    {cardEnabled ? "ACTIVE" : "DISABLED"}
                  </Typography>
                </Box>
                <Typography
                  mt={4}
                  fontSize={20}
                  letterSpacing={3}
                  fontWeight={700}
                >
                  •••• •••• •••• ••••
                </Typography>
                <Typography mt={1} fontSize={12} sx={{ opacity: 0.85 }}>
                  TA Wallet · RuPay
                </Typography>
              </Box>
 
              {/* Enable / Disable */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography fontWeight={700} fontSize={15}>
                    {cardEnabled ? "Card Enabled" : "Card Disabled"}
                  </Typography>
                  <Typography fontSize={12.5} color="text.secondary">
                    {cardEnabled
                      ? "Temporarily disable transactions"
                      : "Enable to resume transactions"}
                  </Typography>
                </Box>
                {toggling ? (
                  <CircularProgress size={22} />
                ) : (
                  <Switch
                    checked={cardEnabled}
                    // Does NOT call the API directly — opens a confirmation first.
                    onChange={handleSwitchChange}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: GREEN },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: GREEN,
                        },
                    }}
                  />
                )}
              </Box>
 
              {/* Permanent block entry */}
              <Box
                onClick={() => setView("permanent")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  borderRadius: 3,
                  border: "1px solid #fee2e2",
                  background: "#fef2f2",
                  cursor: "pointer",
                  transition: "0.15s",
                  "&:hover": { background: "#fee2e2" },
                }}
              >
                <BlockIcon sx={{ color: RED }} />
                <Box flex={1}>
                  <Typography fontWeight={700} fontSize={15} color={RED_DK}>
                    Block Card Permanently
                  </Typography>
                  <Typography fontSize={12.5} color="#b91c1c">
                    Cannot be undone — you'll need a new card
                  </Typography>
                </Box>
                <ArrowBackIcon
                  sx={{ transform: "rotate(180deg)", color: RED }}
                />
              </Box>
            </>
          ) : (
            /* ---------- PERMANENT ---------- */
            <>
              <Box display="flex" alignItems="center" gap={1} mt={1} mb={1.5}>
                <LockOutlinedIcon sx={{ color: GREEN, fontSize: 20 }} />
                <Typography fontWeight={700} fontSize={15}>
                  Enter RuPay PIN
                </Typography>
              </Box>
 
              <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                {pin.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (pinRefs.current[i] = el)}
                    type="password"
                    inputMode="numeric"
                    value={d}
                    maxLength={1}
                    onChange={(e) => handlePinChange(i, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(i, e)}
                    style={{
                      width: 58,
                      height: 58,
                      textAlign: "center",
                      fontSize: 24,
                      fontWeight: 700,
                      borderRadius: 12,
                      border: `1.5px solid ${d ? GREEN : "#cbd5e1"}`,
                      outline: "none",
                      background: "#f8fafc",
                    }}
                  />
                ))}
              </Box>
 
              <Typography fontWeight={700} fontSize={15} mb={1.5}>
                Select reason for blocking
              </Typography>
 
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  mb: 2,
                }}
              >
                {REASONS.map((r) => {
                  const active = reason === r;
                  return (
                    <Box
                      key={r}
                      onClick={() => setReason(r)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.6,
                        borderRadius: 3,
                        cursor: "pointer",
                        border: `1.5px solid ${active ? GREEN : "#e2e8f0"}`,
                        background: active ? "#f0fdf4" : "#fff",
                        transition: "0.15s",
                      }}
                    >
                      <Typography
                        fontSize={14.5}
                        fontWeight={active ? 700 : 500}
                      >
                        {r}
                      </Typography>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          border: `2px solid ${active ? GREEN : "#cbd5e1"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {active && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: GREEN,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
 
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  p: 1.5,
                  borderRadius: 2,
                  background: "#fef2f2",
                  mb: 3,
                }}
              >
                <WarningAmberRoundedIcon
                  sx={{ color: RED, fontSize: 20, flexShrink: 0, mt: "2px" }}
                />
                <Typography fontSize={12.5} color="#b91c1c">
                  If your RuPay card is blocked permanently, you cannot unblock
                  it again. But you can request a new RuPay card.
                </Typography>
              </Box>
 
              <Button
                fullWidth
                disabled={pinStr.length !== 4 || !reason || loading}
                onClick={handleBlockCard}
                sx={btnSx(
                  pinStr.length !== 4 || !reason || loading ? "#cbd5e1" : RED,
                  RED_DK,
                )}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Block Card"
                )}
              </Button>
            </>
          )}
        </Box>
      </Dialog>
 
      {/* ---------------- CONFIRMATION DIALOG (Enable/Disable) ---------------- */}
      <Dialog
        open={confirmOpen}
        onClose={cancelToggle}
        PaperProps={{
          sx: {
            width: 360,
            maxWidth: "90vw",
            borderRadius: "18px",
            p: 3,
            textAlign: "center",
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0,0,0,0.4)",
          },
        }}
      >
        <WarningAmberRoundedIcon
          sx={{
            fontSize: 48,
            color: pendingEnabled ? GREEN : RED,
            mb: 1,
          }}
        />
        <Typography fontWeight={800} fontSize={17} mb={1}>
          {pendingEnabled ? "Enable Card?" : "Disable Card?"}
        </Typography>
        <Typography color="text.secondary" fontSize={14} mb={3}>
          {pendingEnabled
            ? "Are you sure you want to enable this card? Online transactions will be allowed again."
            : "Are you sure you want to disable this card? Online transactions will be temporarily blocked."}
        </Typography>
 
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            fullWidth
            onClick={cancelToggle}
            sx={{
              height: 46,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 14.5,
              color: "#334155",
              background: "#f1f5f9",
              "&:hover": { background: "#e2e8f0" },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            onClick={confirmToggleStatus}
            sx={btnSx(
              pendingEnabled ? GREEN : RED,
              pendingEnabled ? GREEN_DK : RED_DK,
            )}
          >
            Yes
          </Button>
        </Box>
      </Dialog>
 
      {/* SNACKBAR */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
 
const btnSx = (bg, hover) => ({
  height: 50,
  borderRadius: 3,
  textTransform: "none",
  fontWeight: 700,
  fontSize: 15,
  color: "#fff",
  background: bg,
  "&:hover": { background: hover },

  
  "&.Mui-disabled": { color: "#fff" },
});
 
 